/**
 * AppShell - T041, T146, T147
 *
 * Main application layout with responsive sidebar navigation
 * - Desktop: Fixed sidebar
 * - Mobile: Collapsible drawer with hamburger menu
 */
import { type ReactNode, useState, useEffect } from 'react';
import { Link, useMatchRoute, useLocation } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, useLogout } from '../../hooks/useAuth';
import { CommandPalette, LoadingSpinner } from '../ui';
import { MobileBottomNav } from './MobileBottomNav';

interface AppShellProps {
  children: ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    label: 'Kunden',
    path: '/customers',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
  },
  {
    label: 'Rechnungen',
    path: '/invoices',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    label: 'Mahnungen',
    path: '/reminders',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    label: 'Verträge',
    path: '/contracts',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
      </svg>
    ),
  },
  {
    label: 'Audit-Log',
    path: '/audit-log',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: 'Einstellungen',
    path: '/settings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const matchRoute = useMatchRoute();
  const isActive = item.path === '/dashboard'
    ? matchRoute({ to: '/dashboard' }) !== false || matchRoute({ to: '/' }) !== false
    : matchRoute({ to: item.path, fuzzy: true }) !== false;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-5 py-3.5 rounded-lg text-sm font-medium
        transition-colors duration-200
        ${isActive
          ? 'bg-white/10 text-white'
          : 'text-white/60 hover:text-white hover:bg-white/5'
        }
      `}
    >
      <span className={isActive ? 'text-white' : 'text-white/40'}>{item.icon}</span>
      {item.label}
    </Link>
  );
}

// Sidebar content shared between desktop and mobile
function SidebarContent({ onNavClick, isMobile = false }: { onNavClick?: () => void; isMobile?: boolean }) {
  const { user } = useAuth();
  const logout = useLogout();

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-white/[0.06]">
        <Link to="/dashboard" onClick={onNavClick} className="flex items-center gap-3">
          <div className="w-[48px] h-[48px] flex items-center justify-center border border-white/10 rounded-lg shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="13" viewBox="0 0 42 22">
              <path
                fill="currentColor"
                className="text-white/80"
                d="M10.825,0.825 C17.418,0.825 24.012,0.825 30.605,0.825 C30.605,4.121 30.605,7.419 30.605,10.715 C24.012,10.715 17.418,10.715 10.825,10.715 C10.825,7.419 10.825,4.121 10.825,0.825 ZM0.935,10.715 C4.231,10.715 7.529,10.715 10.825,10.715 C10.825,14.011 10.825,17.309 10.825,20.605 C7.529,20.605 4.231,20.605 0.935,20.605 C0.935,17.309 0.935,14.011 0.935,10.715 ZM30.605,10.715 C33.901,10.715 37.199,10.715 40.495,10.715 C40.495,14.011 40.495,17.309 40.495,20.605 C37.199,20.605 33.901,20.605 30.605,20.605 C30.605,17.309 30.605,14.011 30.605,10.715 Z"
              />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white tracking-tight">Armard</span>
            <span className="text-xs text-white/30">CRM</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-5 space-y-2 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink key={item.path} item={item} onClick={onNavClick} />
        ))}
      </nav>

      {/* Version & Links */}
      <div className="px-5 py-3 border-t border-white/[0.06] text-white/30 text-xs leading-relaxed">
        <p>v2.0</p>
        <a
          href="https://armadaquadrat.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:text-white/50 transition-colors !min-h-0"
        >
          armadaquadrat.com
        </a>
        <a
          href="https://armadaquadrat.com/impressum"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:text-white/50 transition-colors !min-h-0"
        >
          Impressum
        </a>
      </div>

      {/* Command palette trigger */}
      <div className="px-5 py-2 border-t border-white/[0.06]">
        {isMobile ? (
          <button
            onClick={() => {
              // Dispatch keyboard event to open command palette
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
              onNavClick?.();
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Befehlspalette
          </button>
        ) : (
          <button
            onClick={() => {
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
            }}
            className="text-xs text-yellow-400/70 hover:text-yellow-400 transition-colors cursor-pointer"
          >
            <kbd className="px-1.5 py-0.5 bg-yellow-500/10 rounded text-yellow-400">⌘K</kbd>
            <span className="ml-1">Befehlspalette</span>
          </button>
        )}
      </div>

      {/* User section */}
      <div
        className="p-3 border-t border-white/[0.06]"
        style={isMobile ? { paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' } : undefined}
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <span className="text-sm text-white/70">
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">{user?.name || user?.email}</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => logout.mutate()}
            className="p-1.5 text-white/40 hover:text-white transition-colors"
            title="Abmelden"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </motion.button>
        </div>
      </div>
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  const { isLoading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Command Palette */}
      <CommandPalette />

      {/* Mobile Header - T146 */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-black/80 backdrop-blur-xl border-b border-white/[0.06] flex items-center justify-between px-4">
        {/* Logo left */}
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 flex items-center justify-center border border-white/10 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 42 22">
              <path
                fill="currentColor"
                className="text-white/80"
                d="M10.825,0.825 C17.418,0.825 24.012,0.825 30.605,0.825 C30.605,4.121 30.605,7.419 30.605,10.715 C24.012,10.715 17.418,10.715 10.825,10.715 C10.825,7.419 10.825,4.121 10.825,0.825 ZM0.935,10.715 C4.231,10.715 7.529,10.715 10.825,10.715 C10.825,14.011 10.825,17.309 10.825,20.605 C7.529,20.605 4.231,20.605 0.935,20.605 C0.935,17.309 0.935,14.011 0.935,10.715 ZM30.605,10.715 C33.901,10.715 37.199,10.715 40.495,10.715 C40.495,14.011 40.495,17.309 40.495,20.605 C37.199,20.605 33.901,20.605 30.605,20.605 C30.605,17.309 30.605,14.011 30.605,10.715 Z"
              />
            </svg>
          </div>
          <span className="text-lg font-bold text-white tracking-tight">Armard</span>
        </div>
        {/* Right side: Command + Hamburger */}
        <div className="flex items-center gap-1">
          {/* Command palette button */}
          <button
            onClick={() => {
              document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
            }}
            className="w-10 h-10 flex items-center justify-center text-yellow-400 hover:text-yellow-300 !min-w-0 transition-colors"
            aria-label="Befehlspalette"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          {/* Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white !min-w-0"
            aria-label="Menu öffnen"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay - T147 */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 z-[45] bg-black/60 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 z-50 w-80 max-w-[85vw] bg-black/95 backdrop-blur-xl border-r border-white/[0.06]"
            >
              {/* Close button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute top-4 right-4 p-2 text-white/40 hover:text-white"
                aria-label="Menu schließen"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <SidebarContent onNavClick={() => setIsMobileMenuOpen(false)} isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-80 border-r border-white/[0.06] bg-black/60 backdrop-blur-lg">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="lg:ml-80 min-h-screen pt-16 lg:pt-0 pb-24 lg:pb-0">
        <div className="px-6 py-8 lg:px-10 lg:py-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - T148 */}
      {!isMobileMenuOpen && <MobileBottomNav />}
    </div>
  );
}

export default AppShell;
